import React, {useState} from 'react'
import moment from 'moment'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import toaster from '../../../../Utils/toaster'
import {useProfileImageUrl} from '../../../../Utils/fileUrlHelper'
import {sendNoShowWarning, type IpiVisNoShowInterpreter} from './_requests'
import {AddNoteModal} from './AddNoteModal'
import {DeleteConfirmationModal} from '../../../common/Models/confirmation-modal/DeleteConfirmationModal'

type Props = {
  items: IpiVisNoShowInterpreter[]
  onViewProfile: (interpreterId: string) => void
}

const NoShowAvatar: React.FC<{avatarUrl?: string | null; name: string}> = ({avatarUrl, name}) => {
  const src = useProfileImageUrl(avatarUrl)
  return (
    <div className='ipivis-noshow__avatar'>
      {src ? <img src={src} alt={name} /> : <span>{name.charAt(0).toUpperCase()}</span>}
    </div>
  )
}

export const TopNoShowInterpreters: React.FC<Props> = ({items, onViewProfile}) => {
  const [noteTarget, setNoteTarget] = useState<IpiVisNoShowInterpreter | null>(null)
  const [warningTarget, setWarningTarget] = useState<IpiVisNoShowInterpreter | null>(null)

  const handleConfirmSendWarning = async () => {
    if (!warningTarget) return
    try {
      const msg = await sendNoShowWarning({
        interpreterId: warningTarget.interpreterId,
        noShowCount: warningTarget.totalNoShows,
      })
      toaster('success', msg || 'Warning email sent successfully.')
    } catch (e: any) {
      toaster('error', e?.response?.data?.text || 'Failed to send warning.')
    } finally {
      setWarningTarget(null)
    }
  }

  return (
    <div className='ipivis-noshow'>
      <div className='ipivis-noshow__header'>
        <div className='ipivis-noshow__title-row'>
          <img src={toAbsoluteUrl('/media/icons/ipivis/alert.svg')} alt='' style={{width: 24, height: 24}} />
          <h2 className='ipivis-noshow__title'>Top 3 No-Show Interpreters</h2>
        </div>
        <div className='ipivis-noshow__subtitle'>
          <img src={toAbsoluteUrl('/media/icons/ipivis/calendar.svg')} alt='' style={{width: 16, height: 16}} />
          Based on last 3 months of assignments, or selected filters
        </div>
      </div>

      {items.length === 0 ? (
        <div className='ipivis-empty ipivis-empty--full'>No no-show interpreters in the selected range.</div>
      ) : (
        <div className='ipivis-noshow__list'>
          {items.map((it) => (
            <div key={it.interpreterId} className='ipivis-noshow__row'>
              <div className='ipivis-noshow__identity'>
                <span className={`ipivis-noshow__rank ipivis-noshow__rank--${it.rank <= 3 ? it.rank : 3}`}>
                  #{it.rank}
                </span>
                <NoShowAvatar avatarUrl={it.avatarUrl} name={it.name} />
                <div className='ipivis-noshow__details'>
                  <h4 className='ipivis-noshow__name'>{it.name}</h4>
                  <p className='ipivis-noshow__last'>
                    Last: {it.lastNoShowDate ? moment(it.lastNoShowDate).format('MMM DD, YYYY') : '—'}
                  </p>
                </div>
              </div>

              <div className='ipivis-noshow__metrics'>
                <div className='ipivis-noshow__metric-box'>
                  <div className='ipivis-noshow__metric'>
                    <span className='ipivis-noshow__metric-label'>Total No-Shows</span>
                    <span className='ipivis-noshow__metric-value'>{it.totalNoShows}</span>
                  </div>
                  <div className='ipivis-noshow__metric'>
                    <span className='ipivis-noshow__metric-label'>Rate</span>
                    <span className='ipivis-noshow__rate'>{it.noShowRate}%</span>
                  </div>
                </div>
              </div>

              <div className='ipivis-noshow__actions'>
                <button
                  className='ipivis-noshow__btn ipivis-noshow__btn--ghost'
                  onClick={() => onViewProfile(it.interpreterId)}
                  type='button'
                >
                  <img src={toAbsoluteUrl('/media/icons/ipivis/view-profile.svg')} alt='' style={{width: 16, height: 16}} />
                  View Profile
                </button>
                <button
                  className='ipivis-noshow__btn ipivis-noshow__btn--olive'
                  onClick={() => setNoteTarget(it)}
                  type='button'
                >
                  <img src={toAbsoluteUrl('/media/icons/ipivis/add-note.svg')} alt='' style={{width: 16, height: 16}} />
                  Add Note
                </button>
                <button
                  className='ipivis-noshow__btn ipivis-noshow__btn--danger'
                  onClick={() => setWarningTarget(it)}
                  type='button'
                >
                  <img src={toAbsoluteUrl('/media/icons/ipivis/send-warning.svg')} alt='' style={{width: 16, height: 16}} />
                  Send Warning
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {noteTarget && (
        <AddNoteModal
          show={!!noteTarget}
          interpreterId={noteTarget.interpreterId}
          interpreterName={noteTarget.name}
          onClose={() => setNoteTarget(null)}
        />
      )}

      {warningTarget && (
        <DeleteConfirmationModal
          title='Send Warning'
          content={`Send an automated no-show warning email to ${warningTarget.name}? The email is dispatched immediately from the Ad Astra Interpretation Team.`}
          onSubmit={handleConfirmSendWarning}
          setOpen={(open: any) => {
            if (!open) setWarningTarget(null)
          }}
        />
      )}
    </div>
  )
}
